import React, { useContext, useState, useEffect, useRef, useCallback } from "react";
import { Flex, FlexCenterCol, Form } from "styles/global-style";
import { AuthContext } from "utils/AuthContext";
import Input from "components/Input";
import Button from "components/Button";
import { useNavigate } from "react-router-dom";
import { loadTrialStore, clearTrialStore } from "utils/TrialStore";

import ninja from "imgs/ninja.gif";

export default function SetOrgAndProj() {
  const navigate = useNavigate();
  const { user, supabase, refresh } = useContext(AuthContext);

  const [inputValid, setInputValid] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [trialSnapshot] = useState(() => loadTrialStore());
  const [migrationError, setMigrationError] = useState(null);

  const firstnameRef = useRef();
  const lastnameRef = useRef();
  const newOrgRef = useRef();
  const newProjRef = useRef();
  const orgHasChanged = useRef(false);

  //----------------------------------------------------

  // Replay the trial store into the freshly-created org/project. Best-effort
  // migration: any single failure is logged but does not stop the rest.
  const migrateTrialData = useCallback(
    async (newOrgId, newProjectId, newPersonId, ownerMembershipId) => {
      if (!trialSnapshot) return;
      try {
        const trialUserId = trialSnapshot.user?.id;

        // people: insert all non-trial-user rows as placeholders. Track the
        // local→real id mapping so we can rewrite foreign keys later.
        const personIdMap = new Map();
        if (trialUserId) personIdMap.set(trialUserId, newPersonId);

        for (const p of trialSnapshot.people || []) {
          if (p.id === trialUserId) continue;
          const { data } = await supabase
            .from("people")
            .insert({
              firstname: p.firstname || "",
              lastname: p.lastname || "",
              discipline_id: p.discipline_id || null,
            })
            .select();
          if (data?.[0]) {
            personIdMap.set(p.id, data[0].id);
            await supabase.from("memberships").insert({
              person_id: data[0].id,
              org_id: newOrgId,
              type: "member",
              status: "placeholder",
            });
          }
        }

        // days: insert all days (joins/leaves + day_type rows). day_type IDs
        // from the trial store don't map to real DB day_type IDs, so we
        // drop day_type for now (only joins/leaves preserve correctly).
        const daysToInsert = [];
        for (const d of trialSnapshot.days || []) {
          const newPersonRowId = personIdMap.get(d.person_id);
          if (!newPersonRowId) continue;
          daysToInsert.push({
            person_id: newPersonRowId,
            project_id: newProjectId,
            date: d.date,
            system_type: d.system_type || null,
            day_type: null,
            comment: d.comment || null,
          });
        }
        if (daysToInsert.length) await supabase.from("days").insert(daysToInsert);

        // goals: skip the trial "unplanned" goal (the standard bootstrap
        // already created one). Migrate the rest.
        const goalsToInsert = (trialSnapshot.goals || [])
          .filter((g) => g.type !== "unplanned")
          .map((g) => ({
            project_id: newProjectId,
            type: g.type || "minor",
            name: g.name || null,
            body: g.body || null,
            date: g.date || null,
            color: g.color || null,
            private: g.private || false,
          }));
        if (goalsToInsert.length) await supabase.from("goals").insert(goalsToInsert);

        // settings: per-user rows get the new user_id; project-wide
        // (user_id null) stay null. Skip "project_disciplines" if it matches
        // the default; otherwise migrate.
        for (const s of trialSnapshot.settings || []) {
          await supabase.from("settings").insert({
            project_id: newProjectId,
            user_id: s.user_id == null ? null : newPersonId,
            name: s.name,
            value: s.value,
          });
        }

        // Images: re-upload to real storage and create the matching image rows.
        for (const img of trialSnapshot.images || []) {
          const bucket = img.type === "logo" ? "logos" : "avatars";
          const uploadKey = `${bucket}/${img.url}`;
          const entry = trialSnapshot.uploads?.[uploadKey];
          if (!entry) continue;
          try {
            const resp = await fetch(entry.dataUrl);
            const blob = await resp.blob();
            const ext = (entry.mimeType || "image/png").split("/")[1] || "png";
            const fileName = `${Math.random().toString(16).slice(2)}.${ext}`;
            const newPath = bucket === "logos"
              ? `${fileName}`
              : `${fileName}`;
            const upRes = await supabase.storage.from(bucket).upload(newPath, blob);
            if (upRes.error) continue;
            if (bucket === "avatars") {
              await supabase.from("images").insert({
                type: "avatar",
                url: fileName,
                ownerid: newPersonId,
              });
            } else {
              const { data: imgRow } = await supabase
                .from("images")
                .insert({ type: "logo", url: fileName, org_id: newOrgId })
                .select("id");
              if (imgRow?.[0]?.id) {
                await supabase.from("orgs").update({ image_id: imgRow[0].id }).eq("id", newOrgId);
              }
            }
          } catch (imgErr) {
            console.warn("Trial image migration failed:", imgErr);
          }
        }
      } catch (err) {
        console.warn("Trial migration partial failure:", err);
        setMigrationError("Some trial data couldn't be transferred. You're set up with the basics.");
      }
    },
    [supabase, trialSnapshot]
  );

  const submit = useCallback(async () => {
    setSubmitted(true);

    const org = await supabase.from("orgs").insert({ name: newOrgRef.current.value }).select();
    const project = await supabase.from("projects").insert({ name: newProjRef.current.value, org_id: org.data[0].id }).select();
    const membership = await supabase.from("memberships").insert({ person_id: user.id, org_id: org.data[0].id, type: "owner", status: "active" }).select();

    // Create unplanned goal (only if one doesn't exist)
    const { data: existingUnplanned } = await supabase
      .from("goals")
      .select("id")
      .eq("project_id", project.data[0].id)
      .eq("type", "unplanned")
      .single();
    
    let unplannedGoal;
    if (!existingUnplanned) {
      const { data: newUnplannedGoal } = await supabase.from("goals").insert({ project_id: project.data[0].id, type: "unplanned" }).select().single();
      unplannedGoal = newUnplannedGoal;
    } else {
      unplannedGoal = existingUnplanned;
    }
    
    // Create default story for the unplanned goal
    if (unplannedGoal) {
      const { data: defaultStory } = await supabase
        .from("work")
        .insert({
          title: "Sprint/Story #1 of " + newProjRef.current.value,
          goal_id: unplannedGoal.id,
          project_id: project.data[0].id,
          parent_id: null,
          type: 'story',
          status: null, // stories don't have status
          child_order: [],
          created_by: user.id
        })
        .select()
        .single();

      if (defaultStory) {
        // Update the goal with workitems_order
        await supabase
          .from("goals")
          .update({ workitems_order: [defaultStory.id] })
          .eq("id", unplannedGoal.id);
      }
    }
    
    await supabase
      .from("people")
      .update({ firstname: firstnameRef.current.value, lastname: lastnameRef.current.value, current_org_id: org.data[0].id, current_proj_id: project.data[0].id })
      .eq("id", user.id);

    // Only insert the default "joins" day when we're not migrating a trial
    // (trial already has the join day; we'd duplicate otherwise).
    if (!trialSnapshot) {
      await supabase.from("days").insert({ person_id: user.id, date: new Date().getNextWeekday().toISOString(), system_type: "joins", project_id: project.data[0].id });
    }

    // Replay any trial data into the freshly-created org/project before
    // refreshing the session.
    if (trialSnapshot) {
      await migrateTrialData(org.data[0].id, project.data[0].id, user.id, membership?.data?.[0]?.id);
      clearTrialStore();
    }

    refresh();
  }, [supabase, refresh, user, trialSnapshot, migrateTrialData]);

  const validateInput = useCallback((e) => {
    const _firstname = firstnameRef.current.value.trim();
    const _lastname = lastnameRef.current.value.trim();
    const firstSet = _firstname && _lastname && newOrgRef.current.value.trim() && newProjRef.current.value.trim();
    if (e.target !== newOrgRef.current && !orgHasChanged.current) {
      setOrgName(_firstname ? firstnameRef.current.value.trim() + "'s Org" : "");
    } else {
      orgHasChanged.current = true;
    }
    setInputValid(firstSet);
  }, []);

  //----------------------------------------------------

  useEffect(() => {
    if (user.org && user.proj) navigate("/auth");

    // Prefer the trial user's firstname/lastname/project name if present.
    const trialUser = trialSnapshot?.people?.find((p) => p.id === trialSnapshot?.user?.id);
    const trialProject = trialSnapshot?.projects?.[0];

    const firstname = trialUser?.firstname && trialUser.firstname !== "You" ? trialUser.firstname : user.firstname;
    const lastname = trialUser?.lastname || user.lastname;
    const projectName = trialProject?.name && trialProject.name !== "Your Project" ? trialProject.name : "";

    if (firstname && firstnameRef.current) firstnameRef.current.value = firstname;
    if (lastname && lastnameRef.current) lastnameRef.current.value = lastname;
    if (firstname && newOrgRef.current) newOrgRef.current.value = firstname + "'s Org";
    if (projectName && newProjRef.current) newProjRef.current.value = projectName;

    // Trigger initial validation after pre-fill.
    if (firstname || lastname || projectName) {
      const valid = !!(
        firstnameRef.current?.value?.trim() &&
        lastnameRef.current?.value?.trim() &&
        newOrgRef.current?.value?.trim() &&
        newProjRef.current?.value?.trim()
      );
      setInputValid(valid);
      if (firstname && !orgHasChanged.current) setOrgName(firstname + "'s Org");
    }

    newProjRef.current?.focus();
  }, [user, navigate, trialSnapshot]);

  return (
    <FlexCenterCol>
      <h1>New beginnings!</h1>
      <img alt="ninja" src={ninja} style={{ height: 200 }} />
      {trialSnapshot && (
        <p style={{ maxWidth: 480, textAlign: "center", opacity: 0.85, marginTop: -10 }}>
          We'll move everything from your trial into your new account.
        </p>
      )}
      {migrationError && (
        <p style={{ color: "#ff9966", fontSize: 13, maxWidth: 480, textAlign: "center" }}>{migrationError}</p>
      )}
      <Form width={500}>
        <Flex>
          <Input caption="First name" ref={firstnameRef} onChange={validateInput} disabled={submitted} white autoFocus left value={user?.firstname || ""} />
          <Input caption="Last name" ref={lastnameRef} onChange={validateInput} disabled={submitted} white left value={user?.lastname || ""} />
        </Flex>
        <Input caption="New organization" onChange={validateInput} ref={newOrgRef} disabled={submitted} white value={orgName} left />
        <Input caption="New project" onChange={validateInput} ref={newProjRef} disabled={submitted} white left />
        <br />
        <br />
        <Button text="Let's GO!" disabled={!inputValid || submitted} callback={submit} large />
      </Form>
      <br />
      <br />
    </FlexCenterCol>
  );
}
